import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/formatters.js';

const colorMap = {
  primary: '#1976d2',
  secondary: '#dc004e',
  success: '#2e7d32',
  error: '#d32f2f',
  warning: '#ed6c02',
  info: '#0288d1',
  default: '#757575',
};

function LeadStageList({
  leadStages = [],
  loading = false,
  onAdd,
  onEdit,
  onDelete,
  onView,
  onImport,
  selectedLeadStage,
  onSelectionChange,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [colorFilter, setColorFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const filteredLeadStages = leadStages.filter((leadStage) => {
    const matchesSearch = leadStage.name?.toLowerCase().includes(searchTerm.toLowerCase())
      || leadStage.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesColor = colorFilter === 'all' || leadStage.color === colorFilter;

    return matchesSearch && matchesColor;
  });

  const handleExport = () => {
    const csvContent = [
      ['Name', 'Description', 'Color', 'Order', 'Created At'],
      ...filteredLeadStages.map((leadStage) => [
        leadStage.name || '',
        leadStage.description || '',
        leadStage.color || '',
        leadStage.order || '',
        leadStage.createdAt ? formatDate(leadStage.createdAt) : '',
      ]),
    ].map((row) => row.map((field) => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_stages.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Stage Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'color',
      headerName: 'Color',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value}
          variant="outlined"
          sx={{
            backgroundColor: colorMap[params.value] || colorMap.default,
            color: 'white',
            '& .MuiChip-label': {
              color: 'white',
            },
          }}
        />
      ),
    },
    {
      field: 'order',
      headerName: 'Order',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => onView(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEdit(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => onDelete(params.row)}
          color="error"
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      {/* Header with search and filters */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" component="h2">
            Lead Stages (
            {filteredLeadStages.length}
            )
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Export Lead Stages">
            <IconButton
              onClick={handleExport}
              disabled={filteredLeadStages.length === 0}
              color="primary"
              size="small"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Lead Stage">
            <IconButton
              onClick={onAdd}
              color="primary"
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search stages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Color</InputLabel>
            <Select
              value={colorFilter}
              label="Color"
              onChange={(e) => setColorFilter(e.target.value)}
            >
              <MenuItem value="all">All Colors</MenuItem>
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="default">Default</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Import Lead Stages">
            <IconButton onClick={onImport} color="primary">
              <MoreIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* DataGrid */}
      <DataGrid
        rows={filteredLeadStages}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        checkboxSelection
        disableRowSelectionOnClick
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: false,
          },
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
}

export default LeadStageList;
